import * as Sentry from '@sentry/react';
import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { MILLISECONDS_IN_A_MINUTE, TDR, toLuxon } from 'tdr-common';
import { getReservationOnSnapshot } from '../../../api/getReservation';
import { CheckoutURLParams } from '../../../common/types';
import { formatTitle } from '../../document-title/DocumentTitle';
import { analytics } from '../../../helpers/analytics';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { ConfirmationTimeout } from '../../../pages/errors/ConfirmationTimeout';
import { RestaurantGuardOutletContext } from '../../../routes/RestaurantGuard';
import { BookingConfirmed, Loading, RequestDenied, RequestSubmitted } from '../../confirmation';
import { ConfirmationProps } from '../../confirmation/types';
import { useTranslation } from 'react-i18next';
import { useGTMAnalytics } from '../../../helpers/gtm/surface/useGTMAnalytics';
import { itemTypeToCategory } from '../../../helpers/gtm/surface/events';

export const Confirmation = () => {
	const { t, i18n } = useTranslation();
	const params = useParams<CheckoutURLParams>();

	const { restaurant } = useOutletContext<RestaurantGuardOutletContext>();
	const gtm = useGTMAnalytics();

	const venueName = restaurant?.displayName ?? restaurant?.name;
	useDocumentTitle(formatTitle(t('titles.document_title_booking_confirmed'), venueName));

	const [booking, setBooking] = useState<TDR.Reservation>();
	const [loading, setLoading] = useState(booking === undefined);
	const [fetchBookingError, setFetchBookingError] = useState(false);
	const hasEmittedGtmRef = useRef(false);

	const luxonBookingTime = toLuxon(booking?.time, booking?.timezone);
	const displayDate = luxonBookingTime?.setLocale(i18n.language === 'fr' ? 'fr-CA' : 'en').toLocaleString({ month: 'long', day: 'numeric' });
	const displayTime = luxonBookingTime?.toFormat('h:mm a');

	useEffect(() => {
		let unsubscribe: () => void;
		let timeoutId: NodeJS.Timeout;

		const fetchBooking = async () => {
			setLoading(true);
			try {
				unsubscribe = getReservationOnSnapshot(params.checkoutId, (doc) => {
					if (doc.exists()) {
						setBooking(doc.data() as TDR.Reservation);
						clearTimeout(timeoutId);
						setLoading(false);
					}
				});

				timeoutId = setTimeout(() => {
					setLoading(false);
					setFetchBookingError(true);
					throw new Error(
						`Confirm booking timeout - failed to find reservation with id: ${params.checkoutId} within the alloted time.`
					);
				}, 2 * MILLISECONDS_IN_A_MINUTE);
			}
			catch (error) {
				const eventID = Sentry.captureException(error);
				analytics.track('Booking Confirmation Timed Out', {
					sentryErrorID: eventID,
					checkoutId: params.checkoutId
				});
			}
		};

		fetchBooking();

		return () => {
			if (unsubscribe) {
				unsubscribe();
			}
			clearTimeout(timeoutId);
		};
	}, [params.checkoutId, restaurant]);

	// Track GTM events — emit once only.
	// onSnapshot fires on every document change (status updates, admin notes, etc.)
	// so we guard with a ref to prevent inflating conversion counts.
	useEffect(() => {
		if (!booking || hasEmittedGtmRef.current) {
			return;
		}
		hasEmittedGtmRef.current = true;

		const luxonTime = toLuxon(booking.time, booking.timezone);
		const date = luxonTime?.toISODate() || '';
		const time = luxonTime?.toFormat('HH:mm') || '';

		gtm.bookingConfirmation({ bookingId: booking.id, guests: booking.guests, date, time });

		if (booking.price > 0) {
			gtm.purchase({
				bookingId: booking.id,
				transactionId: booking.id,
				value: booking.price,
				items: (booking.items || []).map((item) => ({
					id: item.id,
					name: item.label || item.id,
					price: item.price || 0,
					quantity: item.quantity || 1,
					category: itemTypeToCategory(item.type)
				})),
				guests: booking.guests,
				date,
				time
			});
		}
	}, [booking, gtm]);

	if (fetchBookingError) {
		return <ConfirmationTimeout sessionId={params.checkoutId} restaurantSlug={restaurant.slug} />;
	}

	if (loading || !booking) {
		return <Loading key={i18n.language} />;
	}

	const { AdminActionRequired, GuestActionRequired, Sent, Denied } = TDR.Reservation.LargeGroupStatus;
	const status = booking.largeGroupOptions?.largeGroupStatus;

	const isPendingRequest = booking.isLargeGroup && (status === AdminActionRequired || status === GuestActionRequired || status === Sent);
	const isDeniedRequest = booking.isLargeGroup && status === Denied;

	const props: ConfirmationProps = {
		linkTo: `/${restaurant.slug}/explore`,
		guests: booking.guests,
		time: displayTime,
		date: displayDate,
		restaurantName: restaurant.displayName || restaurant.name,
		bookingId: booking.id,
		email: booking.email
	};

	if (isDeniedRequest) {
		return <RequestDenied {...props} />;
	}

	return isPendingRequest ? <RequestSubmitted {...props} /> : <BookingConfirmed {...props} />;
};
